'use client';

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type CanonicalTheme = 'light' | 'dark';
/** Canonical runtime values are light/dark; legacy strings are accepted only for migration. */
export type AppTheme = CanonicalTheme | 'modern' | 'terminal';

type ThemeContextValue = {
  theme: CanonicalTheme;
  setTheme: (theme: AppTheme) => void;
};

const storageKey = 'webapp.theme';
const defaultTheme: CanonicalTheme = 'light';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function normalizeTheme(value: string | null): CanonicalTheme {
  if (value === 'dark' || value === 'terminal') return 'dark';
  return 'light';
}

function readStoredTheme(): CanonicalTheme {
  if (typeof window === 'undefined') return defaultTheme;
  const stored = window.localStorage.getItem(storageKey);
  const normalized = normalizeTheme(stored);
  if (stored !== normalized) window.localStorage.setItem(storageKey, normalized);
  return normalized;
}

function applyTheme(theme: CanonicalTheme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<CanonicalTheme>(defaultTheme);

  useEffect(() => {
    const storedTheme = readStoredTheme();
    setThemeState(storedTheme);
    applyTheme(storedTheme);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    setTheme(nextTheme) {
      const normalized = normalizeTheme(nextTheme);
      setThemeState(normalized);
      window.localStorage.setItem(storageKey, normalized);
      applyTheme(normalized);
    },
  }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  return context ?? {
    theme: defaultTheme,
    setTheme(nextTheme: AppTheme) {
      if (typeof window === 'undefined') return;
      const normalized = normalizeTheme(nextTheme);
      window.localStorage.setItem(storageKey, normalized);
      applyTheme(normalized);
    },
  };
}
